import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AvatarVideoData,
  AvatarVoiceVideoData,
  VideoSuiteAppInterface,
} from "../../../../Types/VideoSuite-Types";
import { Box, Typography } from "@mui/material";
import { AppActionButton } from "../../../ImageSuite/Components/Shared/AppActionButton";
import OutputVideo from "../Shared/OutputVideo";
import SignalRService from "../../../one-query/services/SignalRService";
import {
  GetAvatarsRequestData,
  useGenerateAvatarVideo,
  useGenerateRandomScript,
  useGetAvatars,
  useGetAvatarsVoices,
} from "../../../../store/Apis/SuitesApps/VideoSuiteApi";
import { useAppSelector } from "../../../../store/hooks";
import { AvatarCard } from "../Shared/AvatarCard";
import { SelectBtn } from "../Shared/SelectBtn";
import { SelectAvatarModal } from "../Shared/SelectAvatarModal";
import { SelectVoiceToneModal } from "../Shared/SelectVoiceToneModal";
import { ScriptCard } from "../Shared/ScriptCard";
import { AddScriptModal } from "../Shared/AddScriptModal";
import VideoAppSettings from "../Shared/VideoAppSettings";
import { AvatarModalType } from "../Shared/VideoSuiteHelper";
import { appContainerStyle, appInputOutputContainerStyle } from "../../../ImageSuite/Components/Shared/styles";

const AvatarVideoApp = ({
  videoSettings,
  activeAppProps,
  generatedPrompt,
  setInputValue,
  handleSetIsLoading,
  toggleLockSeed,
  handleModelSettingChanges,
  IsGeneratePromptLoading,
  handelGenerateRandomPrompt,
  getErrorMessage,
  isVideoPromptImproving,
  handelImprovePrompt,
}: VideoSuiteAppInterface) => {
  const outputVideoRef = useRef<HTMLVideoElement | null>(null)
  const [avatarsFiltration, setAvatarsFiltration] = useState<GetAvatarsRequestData>()
  const [avatarsVoicesFiltration, setAvatarsVoicesFiltration] = useState<GetAvatarsRequestData>()
  const { mutate: GenerateRandomScript } = useGenerateRandomScript();
  const {
    mutate: GenerateAvatarVideo,
    isPending: isPendingAvatarVideoGenerating,
    error,
  } = useGenerateAvatarVideo();
  const appSuiteCosts = useAppSelector((s) => s.User.AppSuiteCosts);
  const [showMoreSettings, setShowMoreSettings] = useState<boolean>(false);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showVoiceToneModal, setShowVoiceToneModal] = useState(false);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [videoAvatars, setVideoAvatars] = useState<AvatarVideoData[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarVideoData>();
  const [videoAvatarsVoices, setVideoAvatarsVoices] = useState<
    AvatarVoiceVideoData[]
  >([]);
  const [selectedVoiceTone, setSelectedVoiceTone] =
    useState<AvatarVoiceVideoData>();
  const [modalType, setModalType] = useState(AvatarModalType.Avatar);
  const {
    data: VideoAvatars,
    isSuccess: IsVideoAvatarsSuccess,
    isPending: IsVideoAvatarsLoading,
  } = useGetAvatars(avatarsFiltration);
  const {
    data: VideoAvatarsVoices,
    isSuccess: IsVideoAvatarsVoicesSuccess,
    isPending: IsVideoAvatarsVoicesLoading,
  } = useGetAvatarsVoices(avatarsVoicesFiltration);

  useEffect(() => {
    setAvatarsFiltration({});
    setAvatarsVoicesFiltration({});
  }, []);

  useEffect(() => {
    if (VideoAvatars && IsVideoAvatarsSuccess) {
      setVideoAvatars(VideoAvatars.avatars);
      if (!selectedAvatar) {
        const sampleAvatar = VideoAvatars.avatars?.find(
          (avatar: AvatarVideoData) =>
            avatar.avatar_id === activeAppProps.example.avatarId
        );
        setSelectedAvatar(sampleAvatar);
      }
    }
    if (VideoAvatarsVoices && IsVideoAvatarsVoicesSuccess) {
      setVideoAvatarsVoices(VideoAvatarsVoices.voices);
      if (!selectedVoiceTone) {
        const sampleAvatarVoice = VideoAvatarsVoices.voices?.find(
          (avatar: AvatarVoiceVideoData) =>
            avatar.voice_id === activeAppProps.example.voiceId
        );
        setSelectedVoiceTone(sampleAvatarVoice);
      }
    }
  }, [
    VideoAvatars,
    IsVideoAvatarsSuccess,
    VideoAvatarsVoices,
    IsVideoAvatarsVoicesSuccess,
  ]);

  useEffect(() => {
    if (isPendingAvatarVideoGenerating) {
      handleSetIsLoading(true);
    }
  }, [isPendingAvatarVideoGenerating]);

  useEffect(() => {
    if (error) {
      getErrorMessage(error);
    }
  }, [error]);

  const handleGenerationAction = async () => {
    const connectionId = await SignalRService.getInstance().getConnectionId();
    const videoGeneratorRequest = {
      connectionId: connectionId,
      prompt: activeAppProps?.inputValue,
      ratio: activeAppProps?.settings.selectedSize || "",
      duration: activeAppProps?.settings.selectedDuration || "",
      style: activeAppProps?.settings.selectedStyle.value || "",
      resolution: activeAppProps?.settings.selectedMode || "",
      model: activeAppProps?.settings.selectedModel.name || "",
      version: activeAppProps?.settings.selectedModel.version || "",
      avatarId: selectedAvatar?.avatar_id || "",
      voiceId: selectedVoiceTone?.voice_id || "",
    };
    if (selectedAvatar && selectedVoiceTone) {
      GenerateAvatarVideo(videoGeneratorRequest);
    }
  };

  const handleShowStyleModal = () => {
    setModalType(AvatarModalType.Style);
    setShowStyleModal(true);
  };

  const handleShowAvatarModal = () => {
    setModalType(AvatarModalType.Avatar);
    setShowStyleModal(true);
  };

  const handleCloseStyleModal = () => {
    setShowStyleModal(false);
  };

  const handleShowVoiceToneModal = () => {
    setShowVoiceToneModal(true);
  };

  const handleCloseVoiceToneModal = () => {
    setShowVoiceToneModal(false);
  };

  const handleShowScriptModal = () => {
    setShowScriptModal(true);
  };

  const handleCloseScriptModal = () => {
    setShowScriptModal(false);
  };

  useEffect(() => {
    if (generatedPrompt) {
      setInputValue(generatedPrompt);
    }
  }, [generatedPrompt]);

  const avatarsList = useMemo(() => {
    if (modalType === AvatarModalType.Avatar) {
      const uniqueAvatars = videoAvatars.reduce<AvatarVideoData[]>(
        (list, avatar) => {
          const baseName = avatar.avatar_name?.split(" ")[0];
          if (
            baseName &&
            !list.some((existing: AvatarVideoData) =>
              existing.avatar_name?.split(" ")[0] === baseName
            )
          ) {
            list.push(avatar);
          }
          return list;
        },
        []
      );
      return uniqueAvatars;
    } else {
      const baseName = selectedAvatar?.avatar_name?.split(" ")[0] || "";
      return videoAvatars?.filter((avatar) =>
        avatar.avatar_name?.split(" ")[0] === baseName
      );
    }
  }, [modalType, videoAvatars]);

  return (
    <Box sx={appContainerStyle}>
        <Box className="image-suites-input-header">
          <Typography className="image-suites-input-header-title">
            Create realistic talking avatars like you speak{" "}
          </Typography>
        </Box>
        <Box sx={appInputOutputContainerStyle}>
          <Box className="image-suites-input">
            <Box className="container-input-video">
              <Box className="image-suites-input-failed zero-padding">
                <AvatarCard
                  avatar={selectedAvatar}
                              openReplace={handleShowAvatarModal}
                  isLoading={IsVideoAvatarsLoading && !selectedAvatar}
                />
                <Box sx={{ 
                        width: "100%" ,
                        mt: 2,
                        gap: "12px",
                    }}
                    >
                  <SelectBtn
                    btnTitle="Style"
                    btnAction={handleShowStyleModal}
                    selectedItemDesc={selectedAvatar?.gender || ""}
                    selectedItemName={selectedAvatar?.avatar_name || ""}
                    isLoading={IsVideoAvatarsLoading && !selectedAvatar}
                  />
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    my: 2,
                    gap: "12px",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <SelectBtn
                      btnTitle="Voice Tone"
                      btnAction={handleShowVoiceToneModal}
                      selectedItemName={selectedVoiceTone?.name || ""}
                      selectedItemDesc={selectedVoiceTone?.gender || ""}
                      isLoading={IsVideoAvatarsVoicesLoading && !selectedVoiceTone}
                    />
                  </Box>
                </Box>
              </Box>
              <ScriptCard
                setInputValue={setInputValue}
                inputValue={activeAppProps.inputValue}
                handleShowScriptModal={handleShowScriptModal}
                handleGenerateRandomScript={GenerateRandomScript}
                handelImprovePrompt={handelImprovePrompt}
                isPromptImproving={isVideoPromptImproving}
              />
              <VideoAppSettings
                selectedVideoSettings={{
                  selectedSize: activeAppProps?.settings.selectedSize || "",
                  selectedModel: activeAppProps?.settings.selectedModel || "",
                  selectedMode: activeAppProps?.settings.selectedMode || "",
                  selectedDuration: activeAppProps?.settings.selectedDuration || "",
                  selectedStyle: activeAppProps?.settings.selectedStyle,
                  seed: activeAppProps?.settings.seed || 0,
                }}
                openLockSeed={activeAppProps?.openLockSeed}
                toggleLockSeed={toggleLockSeed}
                handleModelSettingChanges={handleModelSettingChanges}
                showMoreSettings={showMoreSettings}
                setShowMoreSettings={setShowMoreSettings}
                videoAppSettings={videoSettings}
                showSetting={{
                  models: false,
                  dimensions: true,
                  modes: false,
                  seed: false,
                  quality: false,
                  duration: false,
                  style: false,
                }}
              />
            </Box>
            <AppActionButton
              btnTitle="Create Video"
              creditsCount={activeAppProps?.settings?.creditsNeeded??appSuiteCosts?.Video}
              appMethodFire={handleGenerationAction}
              inputValue={activeAppProps?.inputValue}
              IsGeneratePromptLoading={IsGeneratePromptLoading}
              isGenerateButtonDisabled={activeAppProps?.isLoading}
            />
          </Box>
          <Box className="image-suites-output">
            <Box className="image-suites-output-result">
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography className="image-suites-output-result-placeholder">
                    {activeAppProps?.isLoading || activeAppProps?.outputUrl
                      ? "Output"
                      : "Sample Output"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <OutputVideo
                    ref={outputVideoRef}
                    videoUrl={
                      activeAppProps?.outputUrl
                        ? activeAppProps?.outputUrl
                        : activeAppProps?.example.result
                    }
                    isLoading={activeAppProps?.isLoading}
                    generateTime={activeAppProps?.settings.generateTime}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <SelectAvatarModal
          displayGender={modalType === AvatarModalType.Avatar}
          open={showStyleModal}
          avatars={avatarsList || []}
          handleClose={handleCloseStyleModal}
          handleSelectAvatar={setSelectedAvatar}
          selectedAvatar={selectedAvatar}
          isLoading={IsVideoAvatarsLoading}
        />
      {showScriptModal && (
        <AddScriptModal
          open={showScriptModal}
          setInputValue={setInputValue}
          handleClose={handleCloseScriptModal}
          inputValue={activeAppProps.inputValue}
          handleGenerateRandomScript={GenerateRandomScript}
          handelImprovePrompt={handelImprovePrompt}
          isPromptImproving={isVideoPromptImproving}
        />
      )}
      <SelectVoiceToneModal
        open={showVoiceToneModal}
        avatarVoices={videoAvatarsVoices}
        selectedVoiceTone={selectedVoiceTone}
        handleClose={handleCloseVoiceToneModal}
        handleSelectVoiceTone={setSelectedVoiceTone}
        selectedAvatar={selectedAvatar}
        isLoading={IsVideoAvatarsVoicesLoading}
      />
    </Box>
  );
};

export default AvatarVideoApp;
